import { APP_INITIALIZER, ApplicationConfig, LOCALE_ID, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NavigationEnd, NavigationError, Router, provideRouter, withInMemoryScrolling, withNavigationErrorHandler } from '@angular/router';
import { filter } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { AuthStoreService } from './core/services/auth-store.service';

registerLocaleData(localeEs, 'es-ES');

const STALE_CHUNK_RELOAD_KEY = 'app:stale-chunk-reload';

function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk .* failed/i.test(message);
}

function handleNavigationError(event: NavigationError): void {
  if (!isStaleChunkError(event.error)) {
    return;
  }

  // The chunk hashes referenced by the currently loaded index.html no longer match
  // what the server has (a new version was deployed). A single hard reload fetches
  // the fresh index.html and matching chunks; the guard avoids a reload loop if the
  // app is broken for another reason.
  if (sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)) {
    return;
  }

  sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1');
  window.location.href = event.url;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withNavigationErrorHandler(handleNavigationError),
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    { provide: LOCALE_ID, useValue: 'es-ES' },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AuthStoreService],
      useFactory: (authStore: AuthStoreService) => () => authStore.initialize(),
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [Router],
      useFactory: (router: Router) => () => {
        router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
          sessionStorage.removeItem(STALE_CHUNK_RELOAD_KEY);
        });
      },
    },
  ],
};
